import { DateTime } from 'luxon'
import { isOdmonEnrolledCamera } from '../../../utils/utils'
import {
  AutomatedInfractionTypeLabel,
  BadgeStatus,
  IgnoreReasonLabel,
  ModerationEventStatus,
  NOT_AVAILABLE,
  UNKNOWN_STATE
} from './cameraOverviewEnums'
import {
  AmbiguousCameraReasonType,
  AutomatedDeviceInfractionDetailsEnforcementLevel,
  AutomatedDeviceInfractionDetailsType,
  Camera,
  CameraNotEnrolledReasonType,
  CustomerCommunicationActionType,
  DeviceInfractionDetailsInfractionType,
  DuplicateClearanceReasonType,
  GBAddressCountry,
  IgnoredAction,
  IgnoredActionType,
  LocationNotSubscribedReasonType,
  ModerationEvent,
  ModerationEventSource,
  UnenrollCameraActionType
} from '../../../api/models'
import { isWithinLastMs } from '../../../utils/dateUtils'
import { Duration } from 'luxon'
import { lookupCameraDetails } from '../../../utils/cameraUtils'

const middleDot = '•'

const enforcementLevelDisplayName: Record<AutomatedDeviceInfractionDetailsEnforcementLevel, string> = {
  [AutomatedDeviceInfractionDetailsEnforcementLevel.FirstWarning]: `Warned ${middleDot} 1st`,
  [AutomatedDeviceInfractionDetailsEnforcementLevel.FinalWarning]: `Warned ${middleDot} Final`,
  [AutomatedDeviceInfractionDetailsEnforcementLevel.Unenroll]: 'Unenrolled'
}

const ignoreReasonLabel: Record<string, string> = {
  [DuplicateClearanceReasonType.DuplicateClearance]: IgnoreReasonLabel.DuplicateClearance,
  [CameraNotEnrolledReasonType.CameraNotEnrolled]: IgnoreReasonLabel.CameraNotEnrolled,
  [LocationNotSubscribedReasonType.LocationNotSubscribed]: IgnoreReasonLabel.LocationNotSubscribed,
  [AmbiguousCameraReasonType.AmbiguousCamera]: IgnoreReasonLabel.AmbiguousCamera
}

export const getIgnoreReasonLabel = (reasonType: string): string => ignoreReasonLabel[reasonType] ?? reasonType

export const getBundledEventsCountLabel = (count: number): string => `${count} Bundled Event${count === 1 ? '' : 's'}`

export const getMostRecentFailedEventId = (events: ModerationEvent[]): string | undefined => {
  const mostRecentFailed = events.reduce<ModerationEvent | undefined>((latest, event) => {
    if (event.status !== ModerationEventStatus.Failed) return latest
    if (!latest) return event

    return DateTime.fromISO(event.createdAt).toMillis() > DateTime.fromISO(latest.createdAt).toMillis() ? event : latest
  }, undefined)

  return mostRecentFailed?.id
}

// Handles values shaped like "sampl|ss-onelogin|first.last@simplisafe.com" -> "First Last"
export const formatDocumentedByName = (documentedBy: string): string => {
  const segments = documentedBy.split('|')
  const emailSegment = segments[2]
  const localPart = emailSegment?.split('@')[0]
  if (!localPart) return documentedBy

  return localPart
    .split('.')
    .filter(Boolean)
    .map(namePart => namePart.charAt(0).toUpperCase() + namePart.slice(1))
    .join(' ')
}

const automatedCameraInfractionTypeLabels: Partial<Record<DeviceInfractionDetailsInfractionType, string>> = {
  [DeviceInfractionDetailsInfractionType.ImproperDevicePlacement]: AutomatedInfractionTypeLabel.ImproperDevicePlacement,
  [DeviceInfractionDetailsInfractionType.InappropriateBehaviorWitnessed]: AutomatedInfractionTypeLabel.InappropriateBehaviorWitnessed
}

export const getEscalationLevel = (event: ModerationEvent): BadgeStatus.Warned | BadgeStatus.Unenrolled => {
  if ('enforcementLevel' in event.details && event.details.enforcementLevel) {
    return event.details.enforcementLevel === AutomatedDeviceInfractionDetailsEnforcementLevel.Unenroll ? BadgeStatus.Unenrolled : BadgeStatus.Warned
  }
  const hasUnenroll = event.actions?.some(action => action.type === UnenrollCameraActionType.UnenrollCamera)
  return hasUnenroll ? BadgeStatus.Unenrolled : BadgeStatus.Warned
}

export const getInfractionTypeLabel = (event: ModerationEvent): string => {
  const ignoredAction = event.actions?.find(action => action.type === IgnoredActionType.Ignored)
  if (ignoredAction && 'reason' in ignoredAction) {
    return getIgnoreReasonLabel(ignoredAction.reason.type)
  }

  if (!('infractionType' in event.details)) return 'Reason not found'

  const infractionType = event.details.infractionType

  if (event.source === ModerationEventSource.Outdoor_Monitoring_Engagement_Handling) {
    return automatedCameraInfractionTypeLabels[infractionType] ?? infractionType.replace(/([a-z])([A-Z])/g, '$1 $2')
  }

  return infractionType.replace(/([a-z])([A-Z])/g, '$1 $2')
}

const getManualEventLabel = (actions: ModerationEvent['actions']): string | undefined => {
  if (actions.some(a => a.type === UnenrollCameraActionType.UnenrollCamera)) return BadgeStatus.Unenrolled
  if (actions.some(a => a.type === CustomerCommunicationActionType.CustomerCommunication)) return BadgeStatus.Warned
  return BadgeStatus.Active
}

export const isIgnoredEvent = (event: ModerationEvent): boolean => event.actions?.some(action => action.type === IgnoredActionType.Ignored) ?? false

export const getEventTypeInfo = (event: ModerationEvent) => {
  const isAutomated = event.details.type === AutomatedDeviceInfractionDetailsType.AutomatedDeviceInfraction
  const enforcementLevel = 'enforcementLevel' in event.details ? event.details.enforcementLevel : undefined
  const enforcementLevelLabel = enforcementLevel ? enforcementLevelDisplayName[enforcementLevel] : undefined
  const manualEventLabel = isAutomated ? undefined : getManualEventLabel(event.actions)
  const isIgnored = isAutomated && isIgnoredEvent(event)
  return { isAutomated, enforcementLevelLabel, manualEventLabel, isIgnored }
}

export const getTemplateName = (event: ModerationEvent | null) => {
  return event?.actions?.find(action => action.type === CustomerCommunicationActionType.CustomerCommunication)?.communicationName || NOT_AVAILABLE
}

export const filterOutdoorCameras = (cameras: Camera[]) => {
  return cameras.filter(camera => lookupCameraDetails(camera.model)?.isOutdoor && camera.supportedFeatures.outdoorMonitoringEnabled)
}

export const determineMostSevereAction = (event: ModerationEvent): string => {
  const actions = event.actions
  if (!actions || actions.length === 0) {
    return UNKNOWN_STATE
  }

  let mostSevereActionTaken = UNKNOWN_STATE

  actions.forEach(action => {
    if (action.type === CustomerCommunicationActionType.CustomerCommunication && mostSevereActionTaken !== BadgeStatus.Unenrolled) {
      mostSevereActionTaken = BadgeStatus.Warned
    } else if (action.type === UnenrollCameraActionType.UnenrollCamera) {
      mostSevereActionTaken = BadgeStatus.Unenrolled
    }
  })
  return mostSevereActionTaken
}

const ENFORCEMENT_WINDOW_MS = Duration.fromObject({ days: 30 }).as('milliseconds')

const AUTOMATED_WARNING_LEVELS = [
  AutomatedDeviceInfractionDetailsEnforcementLevel.FirstWarning,
  AutomatedDeviceInfractionDetailsEnforcementLevel.FinalWarning
]

const getEnforcementLevel = (event: ModerationEvent): AutomatedDeviceInfractionDetailsEnforcementLevel | undefined =>
  'enforcementLevel' in event.details ? event.details.enforcementLevel : undefined

const getFailedEnforcementBadge = (event: ModerationEvent): BadgeStatus.UnenrollFailed | BadgeStatus.WarnFailed | undefined => {
  const enforcementLevel = getEnforcementLevel(event)
  if (enforcementLevel) {
    if (enforcementLevel === AutomatedDeviceInfractionDetailsEnforcementLevel.Unenroll) return BadgeStatus.UnenrollFailed
    return AUTOMATED_WARNING_LEVELS.includes(enforcementLevel) ? BadgeStatus.WarnFailed : undefined
  }

  const manualLabel = getManualEventLabel(event.actions)
  if (manualLabel === BadgeStatus.Unenrolled) return BadgeStatus.UnenrollFailed
  if (manualLabel === BadgeStatus.Warned) return BadgeStatus.WarnFailed
  return undefined
}

const getDuplicateClearanceConflictingEngagementId = (event: ModerationEvent): string | undefined => {
  const ignoredAction = event.actions?.find((a): a is IgnoredAction => a.type === IgnoredActionType.Ignored)
  return ignoredAction?.reason.type === DuplicateClearanceReasonType.DuplicateClearance ? ignoredAction.reason.conflictingEngagementId : undefined
}

const isCompletedAutomatedEnforcement = (event: ModerationEvent): boolean =>
  event.status === ModerationEventStatus.Completed &&
  event.source === ModerationEventSource.Outdoor_Monitoring_Engagement_Handling &&
  !isIgnoredEvent(event)

const hasCompletedUnenrollAction = (events: ModerationEvent[]): boolean =>
  events.some(
    event =>
      event.status === ModerationEventStatus.Completed && event.actions?.some(action => action.type === UnenrollCameraActionType.UnenrollCamera)
  )

const sortByMostRecent = (events: ModerationEvent[]): ModerationEvent[] =>
  [...events].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

/**
 * Derives the badge to show on a camera's card in the Camera Overview page, based on the camera's current ODMON
 * enrollment and its moderation event history from the last 30 days. Precedence order matters: the first matching
 * rule wins. Events older than the enforcement window are excluded entirely, so a camera with no recent history
 * always shows Active.
 */
export const deriveCameraCardBadge = (camera: Camera, events: ModerationEvent[]): BadgeStatus => {
  const cameraEvents = sortByMostRecent(
    events.filter(event => event.details?.device.id === camera.uuid && isWithinLastMs(event.createdAt, ENFORCEMENT_WINDOW_MS))
  )
  const mostRecentEvent = cameraEvents[0]

  if (mostRecentEvent?.status === ModerationEventStatus.Failed) {
    const failedBadge = getFailedEnforcementBadge(mostRecentEvent)
    if (failedBadge === BadgeStatus.WarnFailed) return failedBadge
    if (failedBadge === BadgeStatus.UnenrollFailed && isOdmonEnrolledCamera(camera)) return failedBadge
  }

  if (isOdmonEnrolledCamera(camera) && hasCompletedUnenrollAction(cameraEvents)) {
    return BadgeStatus.PreviouslyUnenrolled
  }

  const mostRecentNonIgnoredEvent = cameraEvents.find(event => !isIgnoredEvent(event))
  const recentLevel =
    mostRecentNonIgnoredEvent && isCompletedAutomatedEnforcement(mostRecentNonIgnoredEvent)
      ? getEnforcementLevel(mostRecentNonIgnoredEvent)
      : undefined
  if (recentLevel === AutomatedDeviceInfractionDetailsEnforcementLevel.FinalWarning) {
    return BadgeStatus.WarnedFinal
  }
  if (recentLevel === AutomatedDeviceInfractionDetailsEnforcementLevel.FirstWarning) {
    return BadgeStatus.WarnedFirst
  }

  if (!isOdmonEnrolledCamera(camera)) {
    return BadgeStatus.Unenrolled
  }

  if (mostRecentNonIgnoredEvent && determineMostSevereAction(mostRecentNonIgnoredEvent) === BadgeStatus.Warned) {
    return BadgeStatus.Warned
  }
  return BadgeStatus.Active
}

export const getCommunicationBody = (event: ModerationEvent | null, country?: string): string => {
  if (!event) {
    return NOT_AVAILABLE
  }

  const communicationAction = event.actions?.find(action => action.type === CustomerCommunicationActionType.CustomerCommunication)
  if (!communicationAction) {
    return NOT_AVAILABLE
  }

  const isGBLocation = country === GBAddressCountry.GB
  const locale: 'en-US' | 'en-GB' = isGBLocation ? 'en-GB' : 'en-US'

  const emailCommunicationBody = communicationAction.content?.email?.body?.[locale]
  const smsCommunicationBody = communicationAction.content?.sms?.body?.[locale]
  const pushCommunicationBody = communicationAction.content?.push?.body?.[locale]
  const inboxCommunicationBody = communicationAction.content?.app?.bodySummary?.[locale]

  if (emailCommunicationBody) {
    return emailCommunicationBody
  }

  if (smsCommunicationBody) {
    return smsCommunicationBody
  }

  if (pushCommunicationBody) {
    return pushCommunicationBody
  }

  if (inboxCommunicationBody) {
    return inboxCommunicationBody
  }

  return NOT_AVAILABLE
}

export type BundledModerationEvent = ModerationEvent & {
  bundledDuplicateClearanceEvents?: ModerationEvent[]
}

const getEngagementId = (event: ModerationEvent): string | undefined => ('engagementId' in event.details ? event.details.engagementId : undefined)

export const getEngagementReviewUrl = (event: ModerationEvent): string | undefined => {
  const engagementId = getEngagementId(event)
  return engagementId ? `${window.location.origin}/locations/${event.locationId}/engagement-review/${engagementId}` : undefined
}

export const bundleDuplicateClearanceEvents = (events: ModerationEvent[]): BundledModerationEvent[] => {
  const parentByEngagementId = new Map<string, ModerationEvent>()
  events.forEach(event => {
    const engagementId = getEngagementId(event)
    if (engagementId) parentByEngagementId.set(engagementId, event)
  })

  const childrenByParentId = new Map<string, ModerationEvent[]>()
  const bundledChildIds = new Set<string>()

  events.forEach(event => {
    const conflictingEngagementId = getDuplicateClearanceConflictingEngagementId(event)
    if (!conflictingEngagementId) return

    const parent = parentByEngagementId.get(conflictingEngagementId)
    if (!parent || parent.id === event.id) return // no matching parent loaded -> stays standalone

    bundledChildIds.add(event.id)
    childrenByParentId.set(parent.id, [...(childrenByParentId.get(parent.id) ?? []), event])
  })

  return events
    .filter(event => !bundledChildIds.has(event.id))
    .map(event => {
      const bundledDuplicateClearanceEvents = childrenByParentId.get(event.id)
      return bundledDuplicateClearanceEvents ? { ...event, bundledDuplicateClearanceEvents } : event
    })
}
